import { useRef } from "react";
import * as Sentry from "@sentry/react";
import { useApiClient } from "@/hooks/useApiClient";
import {
  type PictureTypeData,
  type AllMattersAnalyzeOptions,
} from "@/types/acquirebook";
import { type ReusePictures } from "@/features/acquireBook/utils/pictureRequest";
import { buildPropertyCodeNewAcquireRequest } from "@/features/propertyCodeAcquisition/utils/newAcquireRequest";
import {
  buildPropertyCodeReuseAcquireRequest,
  pickReuseSelections,
} from "@/features/propertyCodeAcquisition/utils/reuseAcquireRequest";
import { buildAcquireReuseEndpoint } from "@/features/propertyCodeAcquisition/api/getAcquireReuse";
import { buildPropertyCodePictureRequestEndpoint } from "@/features/propertyCodeAcquisition/api/getPropertyCodePictureRequestList";
import { postAcquireRequest } from "@/features/propertyCodeAcquisition/api/postAcquireRequest";
import { handlePropertyCodeApiError } from "@/features/propertyCodeAcquisition/utils/handlePropertyCodeApiError";

// 物件コード専用の取得実行 hook。
//
// 地番を含まないフラットな payload（PropertyCodeNewAcquireRequest）を物件コード配下エンドポイント
// （POST /property-codes/{code}/picture-requests）へ送出する。所在/地番はサーバが物件コードから解決する。

/** 同一物件で取得処理が進行中（409）のときに表示するメッセージ。 */
export const ACQUIRE_IN_PROGRESS_MESSAGE =
  "この物件は現在取得処理中です。完了後に再度お試しください。";

/** 取得実行失敗時の汎用メッセージ（5xx・非 Axios・想定外 400 等）。 */
export const ACQUIRE_FAILED_MESSAGE =
  "取得処理に失敗しました。時間をおいて再度お試しください。";

// 取得実行（POST）の送信先は履歴取得（GET）と同一パス（/property-codes/{code}/picture-requests）。
// パス変更時に GET/POST が別パスを叩く不具合や、同名 export の auto-import 取り違えを防ぐため、
// エンドポイント生成は api 層（getPropertyCodePictureRequestList）の buildPropertyCodePictureRequestEndpoint に一本化する。

/** 取得ダイアログから送信ロジックへ渡す取得選択（地番を含まない＝物件コードでリクエストする）。 */
export interface PropertyCodeAcquireSubmitParams {
  /** 取得対象（所有者事項/全部事項/図面系）の選択状態 */
  requestOptions: PictureTypeData;
  /** 所有者事項の同時解析 */
  withOwnerAnalyze: boolean;
  /** 全部事項の同時解析 */
  withAllMattersAnalyze: boolean;
  /** 識別名（案件名。UI 非表示のため空文字固定・契約上空文字可） */
  identificationName: string;
  /** 全部事項解析オプション */
  allMattersAnalyzeOptions?: AllMattersAnalyzeOptions;
  /** リユース選択（新規取得では未使用） */
  reusePictures?: ReusePictures[];
  /** 送信完了/失敗時にローディングを解除するコールバック */
  cancelLoading: () => void;
  /** 送信中フラグの更新（確認ダイアログのキャンセル無効化・外側 Dialog の onClose 抑止） */
  setDisableConfirmModalCancelButton?: (submitting: boolean) => void;
  /** 取得成功時のコールバック（履歴 mutate / クローズの配線） */
  onSuccess?: () => void;
}

/** usePropertyCodeAcquireBookForm の戻り値（コンポーネント名との衝突を避けた命名）。 */
export interface UsePropertyCodeAcquireBookFormReturn {
  /** 取得実行（物件コード配下エンドポイントへ POST）。 */
  handleSubmit: (params: PropertyCodeAcquireSubmitParams) => void;
}

/**
 * 物件コード専用の取得実行 hook。
 * @param propertyCode 物件コード（UUID）
 */
export const usePropertyCodeAcquireBookForm = (
  propertyCode: string
): UsePropertyCodeAcquireBookFormReturn => {
  const { apiClient } = useApiClient();

  // 取得実行（非冪等 POST）の多重送信ガード。外側 SubmitButton の 120 秒タイマーのズレ
  // （初回 POST が完了する前にボタンが復活してしまうタイミング）に依存せず、1 回の POST が完了（成功・失敗）するまで
  // 後続 handleSubmit を確実に弾く。bool フラグだと「1 回目の finally が 2 回目の送信中に抑止を解除してしまう」競合が起きるため ref で防ぐ。
  const isSubmittingRef = useRef(false);

  // 送信先エンドポイント（物件コード配下）。
  const endpoint = buildPropertyCodePictureRequestEndpoint(propertyCode);

  const handleSubmit = (params: PropertyCodeAcquireSubmitParams): void => {
    const {
      requestOptions,
      withOwnerAnalyze,
      withAllMattersAnalyze,
      identificationName,
      allMattersAnalyzeOptions,
      reusePictures = [],
      cancelLoading,
      setDisableConfirmModalCancelButton,
      onSuccess,
    } = params;

    // 既に POST 送信中なら重複送信（重複課金）を防ぐ。2 回目クリックのローディングだけ解除し、
    // 送信中フラグ（抑止ボタン状態）は初回 POST の finally に委ねる。
    if (isSubmittingRef.current) {
      cancelLoading();
      return;
    }
    isSubmittingRef.current = true;

    // 取得方式ラジオでリユースを選んだ対象（reuse_detail_id 非null）が1件以上あるかで送信先エンドポイントを分岐する。
    // いずれも所在/地番/家屋番号/不動産種別/経過日数は含めない（物件コードでリクエストし、サーバが解決する）。
    const hasReuseSelection = pickReuseSelections(reusePictures).length > 0;
    const url = hasReuseSelection
      ? buildAcquireReuseEndpoint(propertyCode)
      : endpoint;
    const payload = hasReuseSelection
      ? buildPropertyCodeReuseAcquireRequest({
          requestOptions,
          reusePictures,
          withOwnerAnalyze,
          withAllMattersAnalyze,
          identificationName,
          allMattersAnalyzeOptions,
        })
      : buildPropertyCodeNewAcquireRequest({
          requestOptions,
          withOwnerAnalyze,
          withAllMattersAnalyze,
          identificationName,
          allMattersAnalyzeOptions,
        });

    setDisableConfirmModalCancelButton?.(true);

    // 401 リトライは GET（getAcquireReuse）のみ実施する。POST は非冪等で、再送が二重取得（重複課金）を
    // 招きうるため現状維持とする。トークン期限切れ時はユーザーに再送を促す。
    postAcquireRequest(apiClient, url, payload)
      .then(() => {
        // POST 自体は成功（{ status: "OK" }）。onSuccess（履歴 mutate 等）内の例外を
        // 「取得失敗」トーストに化けさせないため try/catch で隔離する。
        if (onSuccess) {
          try {
            onSuccess();
          } catch (e) {
            Sentry.captureException(e);
          }
        }
      })
      .catch((e) => {
        handlePropertyCodeApiError(e, {
          fallbackMessage: ACQUIRE_FAILED_MESSAGE,
          conflictMessage: ACQUIRE_IN_PROGRESS_MESSAGE,
          handleBadRequest: true,
        });
      })
      .finally(() => {
        isSubmittingRef.current = false;
        setDisableConfirmModalCancelButton?.(false);
        cancelLoading();
      });
  };

  return { handleSubmit };
};
